import React, { useRef, useState } from 'react';
import {
    Stack,
    Title,
    Text,
    Card,
    Group,
    Button,
    Image,
    ActionIcon,
    FileButton,
    TextInput,
} from '@mantine/core';
import { IconExternalLink, IconUpload, IconX } from '@tabler/icons-react';
import { appConfig } from '../../data/AppConfig';

interface Pitch {
    id: string;
    title: string;
    description: string;
    file: string;
}

// Pitches are served publicly by the backend (watchly.ai/pitches/...) so
// partner-branded links survive deploys and can be shared with prospects.
const PITCHES: Pitch[] = [
    {
        id: 'venues',
        title: 'Venues & Sports Bars',
        description: 'One-pager for venue and sports bar prospects.',
        file: '/pitches/venues-sports-bars.html',
    },
    {
        id: 'sportsbooks',
        title: 'Sportsbooks & Betting Platforms',
        description: 'One-pager for sportsbook and betting platform prospects.',
        file: '/pitches/sportsbooks.html',
    },
];

const pitchUrl = (file: string) => `${appConfig.REACT_APP_BACKEND_URL}${file}`;

const PitchCard: React.FC<{ pitch: Pitch }> = ({ pitch }) => {
    const [logo, setLogo] = useState<string | null>(null);
    const [partner, setPartner] = useState('');
    const resetRef = useRef<() => void>(null);

    const handleFile = (file: File | null) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = e => setLogo(e.target?.result as string);
        reader.readAsDataURL(file);
    };

    const clearLogo = () => {
        setLogo(null);
        resetRef.current?.();
    };

    const handleOpen = () => {
        const slug = partner.trim().toLowerCase();
        if (slug) {
            // Durable, shareable version backed by a committed logo at
            // /pitches/partners/<slug>.png (e.g. ?partner=bww).
            window.open(`${pitchUrl(pitch.file)}?partner=${encodeURIComponent(slug)}`, '_blank');
        } else if (logo) {
            // Ad-hoc logo: passed in the hash fragment, rendered client-side
            // by the pitch page. Not shareable beyond this tab's URL.
            window.open(`${pitchUrl(pitch.file)}#logo=${encodeURIComponent(logo)}`, '_blank');
        } else {
            window.open(pitchUrl(pitch.file), '_blank');
        }
    };

    return (
        <Card shadow="xs" withBorder p="lg">
            <Stack gap="sm">
                <div>
                    <Title order={4}>{pitch.title}</Title>
                    <Text size="sm" c="dimmed" mt={4}>{pitch.description}</Text>
                </div>

                <Group align="flex-end" gap="sm">
                    <Stack gap={4}>
                        <Text size="xs" c="dimmed" fw={500}>Partner slug (committed logo)</Text>
                        <TextInput
                            size="xs"
                            placeholder="e.g. bww"
                            value={partner}
                            onChange={e => setPartner(e.currentTarget.value)}
                            w={140}
                        />
                    </Stack>

                    <Stack gap={4}>
                        <Text size="xs" c="dimmed" fw={500}>Or upload ad-hoc logo</Text>
                        <Group gap="xs" align="center">
                            {logo ? (
                                <>
                                    <Image src={logo} h={28} w="auto" fit="contain" maw={120} />
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        color="gray"
                                        onClick={clearLogo}
                                        aria-label="Remove logo"
                                    >
                                        <IconX size={12} />
                                    </ActionIcon>
                                </>
                            ) : (
                                <FileButton resetRef={resetRef} onChange={handleFile} accept="image/*">
                                    {props => (
                                        <Button
                                            {...props}
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconUpload size={12} />}
                                        >
                                            Upload logo
                                        </Button>
                                    )}
                                </FileButton>
                            )}
                        </Group>
                    </Stack>

                    <Button
                        ml="auto"
                        leftSection={<IconExternalLink size={14} />}
                        onClick={handleOpen}
                    >
                        Open
                    </Button>
                </Group>
            </Stack>
        </Card>
    );
};

const Pitches: React.FC = () => (
    <Stack gap="md" mt="xl">
        <Title order={2}>Pitches</Title>
        {PITCHES.map(pitch => (
            <PitchCard key={pitch.id} pitch={pitch} />
        ))}
    </Stack>
);

export default Pitches;
